import { PRESET_IDS, formatAge, formatDuration } from '@p4p2r0/minesweeper-core';
import type {
  LeaderboardEntry,
  LeaderboardResponse,
  LeaderboardSort,
  LeaderboardWindow,
  LevelLeaderboardEntry,
  LevelLeaderboardResponse,
  MinesweeperClient,
  PresetId,
} from '@p4p2r0/minesweeper-core';
import React, { useEffect, useState } from 'react';

import { useI18n, type StringKey } from '../i18n';

interface LeaderboardProps {
  client: MinesweeperClient | null;
  /** Bumped after a submitted win so the board refetches. */
  refreshKey: number;
}

/** The level-based board first — it's the one ranking that spans every
    difficulty, so it reads as the "overall" standing rather than one more
    option alongside beginner/intermediate/expert. */
type Tab = 'levels' | PresetId;
const TABS: readonly Tab[] = ['levels', ...PRESET_IDS];

const DIFFICULTY_KEYS: Record<PresetId, StringKey> = {
  beginner: 'difficulty.beginner',
  intermediate: 'difficulty.intermediate',
  expert: 'difficulty.expert',
};

const SORT_KEYS: Record<LeaderboardSort, StringKey> = {
  wins: 'board.sort.wins',
  time: 'board.sort.time',
};

const WINDOW_KEYS: Record<LeaderboardWindow, StringKey> = {
  daily: 'board.period.daily',
  weekly: 'board.period.weekly',
  all: 'board.period.all',
};

/**
 * Difficulty is the one choice worth a row of its own — it changes which game
 * you are looking at. Sort and period only reorder the same set, so they sit in
 * a quiet line of selects underneath rather than a second and third row of tabs.
 */
export function Leaderboard({ client, refreshKey }: LeaderboardProps): React.ReactElement {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('levels');
  const [window, setWindow] = useState<LeaderboardWindow>('all');
  const [sort, setSort] = useState<LeaderboardSort>('wins');
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [levelBoard, setLevelBoard] = useState<LevelLeaderboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!client) {
      setBoard(null);
      setLevelBoard(null);
      setError(t('board.offline'));
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request =
      tab === 'levels'
        ? client.levelLeaderboard({ limit: 25 }).then((result) => {
            if (!cancelled) setLevelBoard(result);
          })
        : client.leaderboard(tab, window, { sort, limit: 25 }).then((result) => {
            if (!cancelled) setBoard(result);
          });
    request
      .catch((problem: unknown) => {
        if (!cancelled) setError(problem instanceof Error ? problem.message : 'Could not load.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, tab, window, sort, refreshKey, t]);

  const showYou = board?.you && !board.entries.some((entry) => entry.isYou);
  const showLevelYou = levelBoard?.you && !levelBoard.entries.some((entry) => entry.isYou);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">{t('nav.leaderboard')}</h2>
      </div>

      <div className="segmented" role="tablist" aria-label={t('board.difficulty')}>
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={id === tab}
            className={id === tab ? 'segment active' : 'segment'}
            onClick={() => setTab(id)}
          >
            {id === 'levels' ? t('board.levels') : t(DIFFICULTY_KEYS[id])}
          </button>
        ))}
      </div>

      {tab !== 'levels' ? (
        <div className="board-filters">
          <label className="field">
            <span className="faint">{t('board.sort')}</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as LeaderboardSort)}>
              {(Object.keys(SORT_KEYS) as LeaderboardSort[]).map((id) => (
                <option key={id} value={id}>
                  {t(SORT_KEYS[id])}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="faint">{t('board.period')}</span>
            <select
              value={window}
              onChange={(event) => setWindow(event.target.value as LeaderboardWindow)}
            >
              {(Object.keys(WINDOW_KEYS) as LeaderboardWindow[]).map((id) => (
                <option key={id} value={id}>
                  {t(WINDOW_KEYS[id])}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      <div className="board-body">
        {loading ? <p className="faint small">{t('board.loading')}</p> : null}
        {error ? <p className="error small">{error}</p> : null}

        {tab === 'levels'
          ? levelBoard && !loading && !error
            ? levelBoard.entries.length === 0 ? (
                <p className="faint small">{t('board.empty')}</p>
              ) : (
                <table className="scores">
                  <thead>
                    <tr>
                      <th scope="col">{t('board.rank')}</th>
                      <th scope="col">{t('board.player')}</th>
                      <th scope="col" className="numeric">
                        {t('board.level')}
                      </th>
                      <th scope="col" className="numeric">
                        {t('board.xp')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {levelBoard.entries.map((entry) => (
                      <LevelRow key={entry.playerId} entry={entry} />
                    ))}
                    {showLevelYou && levelBoard.you ? (
                      <>
                        <tr className="gap">
                          <td colSpan={4}>···</td>
                        </tr>
                        <LevelRow entry={levelBoard.you} />
                      </>
                    ) : null}
                  </tbody>
                </table>
              )
            : null
          : board && !loading && !error
            ? board.entries.length === 0 ? (
                <p className="faint small">{t('board.empty')}</p>
              ) : (
                <table className="scores">
                  <thead>
                    <tr>
                      <th scope="col">{t('board.rank')}</th>
                      <th scope="col">{t('board.player')}</th>
                      <th scope="col" className="numeric">
                        {sort === 'wins' ? t('board.wins') : t('board.time')}
                      </th>
                      <th scope="col" className="numeric">
                        {sort === 'wins' ? t('board.best') : t('board.when')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.entries.map((entry, position) => (
                      <Row
                        key={`${entry.playerId}-${entry.achievedAt}`}
                        entry={entry}
                        index={position}
                        sort={sort}
                      />
                    ))}
                    {showYou && board.you ? (
                      <>
                        <tr className="gap">
                          <td colSpan={4}>···</td>
                        </tr>
                        <Row entry={board.you} index={board.entries.length} sort={sort} />
                      </>
                    ) : null}
                  </tbody>
                </table>
              )
            : null}
      </div>
    </section>
  );
}

function LevelRow({ entry }: { entry: LevelLeaderboardEntry }): React.ReactElement {
  return (
    <tr className={entry.isYou ? 'you' : undefined} style={{ '--r': entry.rank - 1 } as React.CSSProperties}>
      <td className={`rank${entry.rank <= 3 ? ` medal-${entry.rank}` : ''}`}>{entry.rank}</td>
      <td className="player">{entry.username}</td>
      <td className="numeric">{entry.level}</td>
      <td className="numeric faint">{entry.totalXp}</td>
    </tr>
  );
}

function Row({
  entry,
  index,
  sort,
}: {
  entry: LeaderboardEntry;
  index: number;
  sort: LeaderboardSort;
}): React.ReactElement {
  return (
    <tr className={entry.isYou ? 'you' : undefined} style={{ '--r': index } as React.CSSProperties}>
      <td className={`rank${entry.rank <= 3 ? ` medal-${entry.rank}` : ''}`}>{entry.rank}</td>
      <td className="player">{entry.username}</td>
      {sort === 'wins' ? (
        <>
          <td className="numeric">{entry.wins}</td>
          <td className="numeric faint">{formatDuration(entry.elapsedMs)}s</td>
        </>
      ) : (
        <>
          <td className="numeric">{formatDuration(entry.elapsedMs)}s</td>
          <td className="numeric faint small">{formatAge(entry.achievedAt)}</td>
        </>
      )}
    </tr>
  );
}
